import {
  AppstoreOutlined,
  CheckOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  PlayCircleFilled,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { Button, Empty, Input, Modal, Pagination, Progress, Select, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  deleteVideoAsset,
  getVideoAssetStats,
  listVideoAssets,
  uploadVideoAsset,
  videoAssetPlaybackUrl,
  videoAssetPosterUrl,
  type SaasApiError,
  type VideoAssetDurationFilter,
  type VideoAssetItem,
  type VideoAssetOriginFilter,
  type VideoAssetResolutionFilter,
  type VideoAssetSourceFilter,
  type VideoAssetStats,
} from '../../api'
import {
  filterSessionScopedNewPickerIds,
  isMobileCaptureSessionPaired,
  type MobileCaptureSessionMeta,
} from '../../hooks/mobileCapturePickerCompletion'
import AssetUploadActionTile from '../AssetUploadActionTile/AssetUploadActionTile'
import MobileCaptureQrModal from '../MobileCaptureQrModal'
import { VistoeraActionConfirm } from '../../ui/action'
import {
  pickerAntdPaginationTotal,
  pickerListRequestParams,
  pickerPageAfterDelete,
  pickerTotalPages,
} from '../ImageAssetPicker/imageAssetPickerContract'
import {
  getCachedVideoPickerStats,
  setCachedVideoPickerStats,
  videoPickerCountCacheKey,
} from './videoAssetPickerCountCache'
import { decrementVideoPickerStats } from './videoAssetPickerOptimisticDelete'
import { isAllowedVideoAssetUploadFile } from '../../videoUploadAllowlist'
import './video-asset-picker.css'

const MOBILE_CAPTURE_POLL_MS = 2000
const PAGE_SIZE_OPTIONS = [24, 48, 96]

type TypeFilter = VideoAssetSourceFilter | 'all'
type OriginFilter = VideoAssetOriginFilter | 'all'
type DurationFilter = VideoAssetDurationFilter | 'all'
type ResolutionFilter = VideoAssetResolutionFilter | 'all'

// VistoeraTASK-00220 — was a private `isAcceptedFile` duplicating
// `ACCEPTED_MIME`/`ACCEPTED_EXT` inline; now the single canonical
// `videoUploadAllowlist.ts` definition, shared with the Canvas OS File
// Drop pipeline (identical accept behavior, zero change for this picker).
const isAcceptedFile = isAllowedVideoAssetUploadFile

function formatDuration(seconds: number | null): string {
  // VISTOERA_VIDEO_ASSETS_V1 / TASK-00120 contract (see
  // `videoIngestion.contract.test.mjs`) — an unknown or zero/negative
  // duration intentionally renders the dash placeholder rather than
  // "0:00". Left as-is; VistoeraTASK-00133 does not change this contract.
  if (seconds == null || Number.isNaN(seconds) || seconds <= 0) return '—'
  const total = Math.round(seconds)
  if (total <= 0) return '—'
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatResolution(w: number | null, h: number | null): string {
  if (!w || !h) return '—'
  const longSide = Math.max(w, h)
  if (longSide >= 3200) return '4K'
  if (longSide >= 1800) return '1080P'
  if (longSide >= 1200) return '720P'
  return `${w}×${h}`
}

function formatCreatedAt(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(locale, { hour12: false })
}

export interface VideoAssetPickerProps {
  open: boolean
  workspaceId: string
  title?: string
  initialSelection?: string | null
  allowUpload?: boolean
  onCancel: () => void
  onConfirm: (item: VideoAssetItem) => void
}

export default function VideoAssetPicker({
  open,
  workspaceId,
  title,
  initialSelection = null,
  allowUpload = true,
  onCancel,
  onConfirm,
}: VideoAssetPickerProps) {
  const { t, i18n } = useTranslation(['canvas', 'video', 'actions'])
  const modalTitle = title ?? t('canvas:mediaCard.selectVideo')

  const [tab, setTab] = useState<'library' | 'upload'>('library')
  const [items, setItems] = useState<VideoAssetItem[]>([])
  // VistoeraTASK-00133 — `null` means "unknown / not yet loaded", distinct
  // from a confirmed `0`. This is what makes LOADING/ERROR states never
  // paint as a fake "共 0 个视频" (see `galleryHeadLabel` below), the exact
  // bug this task's screenshot reproduced.
  const [total, setTotal] = useState<number | null>(null)
  const [stats, setStats] = useState<VideoAssetStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<'recent' | 'oldest'>('recent')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [originFilter, setOriginFilter] = useState<OriginFilter>('all')
  const [durationFilter, setDurationFilter] = useState<DurationFilter>('all')
  const [resolutionFilter, setResolutionFilter] = useState<ResolutionFilter>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(24)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const searchTimer = useRef<number | null>(null)
  const openInitializedRef = useRef(false)
  const listReqRef = useRef(0)
  const statsReqRef = useRef(0)
  const pendingDeleteIdsRef = useRef(new Set<string>())
  const inFlightDeleteCountRef = useRef(0)
  const skipListLoadingOnceRef = useRef(false)
  // Detail panel must never fetch/mount the actual video element just
  // because a card was selected (P20/P25) — only an explicit "play" click
  // does. Keyed by asset id so switching selection always resets it.
  const [playRequestedFor, setPlayRequestedFor] = useState<string | null>(null)

  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const tileFileInputRef = useRef<HTMLInputElement>(null)
  const [tileUploading, setTileUploading] = useState(false)
  const [mobileCaptureOpen, setMobileCaptureOpen] = useState(false)
  const [mobileSessionMeta, setMobileSessionMeta] = useState<MobileCaptureSessionMeta | null>(null)
  const knownVideoIdsRef = useRef<Set<string>>(new Set())
  const mobilePollGenerationRef = useRef(0)
  const mobileCompletionSessionRef = useRef<string | null>(null)
  const mobileSessionMetaRef = useRef<MobileCaptureSessionMeta | null>(null)
  mobileSessionMetaRef.current = mobileSessionMeta

  const selected = useMemo(
    () => items.find((it) => it.id === selectedId) ?? null,
    [items, selectedId],
  )

  const sourceLabel = useCallback(
    (item: VideoAssetItem): string => {
      const kind = item.source_kind
      if (kind === 'ai_generated' || kind === 'continuous_generated' || kind === 'upload') {
        return t(`video:assets.sourceKinds.${kind}`)
      }
      return item.source_kind
    },
    [t],
  )

  const originLabel = useCallback(
    (item: VideoAssetItem): string => {
      if (item.origin_display_name) return item.origin_display_name
      if (item.upload_asset_id) return t('video:assets.sources.upload')
      if (item.origin_is_legacy) return t('video:assets.origin.legacyUnknown')
      return t('video:assets.sources.vistoera_generated')
    },
    [t],
  )

  const loadStats = useCallback(async (opts?: { silent?: boolean }) => {
    if (!workspaceId) return
    const reqId = ++statsReqRef.current
    if (!opts?.silent) setStatsLoading(true)
    try {
      const { data } = await getVideoAssetStats(workspaceId)
      if (statsReqRef.current !== reqId) return
      setStats(data)
      // Stale-while-revalidate (mirrors ImageAssetPicker P4/P9) — next
      // Picker open for this workspace paints this immediately instead of
      // a blank/"—" sidebar while a fresh request revalidates it.
      setCachedVideoPickerStats(videoPickerCountCacheKey(workspaceId), data)
    } catch {
      // Facet failure must not fabricate a "0" — leave counts as unknown
      // (rendered as '—' by `formatFacetCount`), never silently clear a
      // last-known-good value into a wrong number.
    } finally {
      if (statsReqRef.current === reqId && !opts?.silent) setStatsLoading(false)
    }
  }, [workspaceId])

  const hasUploadTilePagination = allowUpload && tab === 'library'
  const showUploadTile = hasUploadTilePagination && page === 1

  const loadGallery = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!workspaceId) return null
      // A background/silent refresh (delete reconcile, mobile-capture poll)
      // must never toggle the visible loading skeleton — only a
      // user-visible open/filter-change request does.
      const silent = opts?.silent || skipListLoadingOnceRef.current
      if (skipListLoadingOnceRef.current) skipListLoadingOnceRef.current = false
      const reqId = ++listReqRef.current
      if (!silent) {
        setLoading(true)
        setError(null)
      }
      try {
        const listParams = pickerListRequestParams(page, pageSize, hasUploadTilePagination)
        const { data } = await listVideoAssets(workspaceId, {
          search: search.trim() || undefined,
          source: typeFilter === 'all' ? undefined : typeFilter,
          origin: originFilter === 'all' ? undefined : originFilter,
          duration: durationFilter === 'all' ? undefined : durationFilter,
          resolution: resolutionFilter === 'all' ? undefined : resolutionFilter,
          sort,
          page: listParams.page,
          page_size: listParams.page_size,
          offset: listParams.offset,
        })
        // Stale-request guard (P18) — a slower older request (e.g. the
        // pre-search-keystroke query) must never clobber a newer one's
        // already-rendered result.
        if (listReqRef.current !== reqId) return undefined
        setItems(data.items)
        setTotal(data.total)
        return data
      } catch (err) {
        if (listReqRef.current !== reqId) return undefined
        if (!silent) {
          // A real API failure is an ERROR state, never a fabricated
          // EMPTY ("0 videos") — `total` is deliberately left untouched
          // here (see the `total: number | null` comment above) so the UI
          // renders "—"/unknown instead of a false zero.
          setError((err as SaasApiError).message || t('canvas:mediaCard.picker.loadFailed'))
          setItems([])
        }
        return null
      } finally {
        if (listReqRef.current === reqId && !silent) setLoading(false)
      }
    },
    [
      workspaceId,
      search,
      typeFilter,
      originFilter,
      durationFilter,
      resolutionFilter,
      sort,
      page,
      pageSize,
      hasUploadTilePagination,
      t,
    ],
  )

  useEffect(() => {
    if (!open) {
      openInitializedRef.current = false
      return
    }
    if (openInitializedRef.current) return
    openInitializedRef.current = true
    setTab('library')
    setSelectedId(initialSelection)
    setPlayRequestedFor(null)
    setPage(1)
    setSearch('')
    setSearchDraft('')
    setTypeFilter('all')
    setOriginFilter('all')
    setDurationFilter('all')
    setResolutionFilter('all')
    setSort('recent')
    setViewMode('grid')
    setUploadFile(null)
    setUploadTitle('')
    setUploadError(null)
    setError(null)
    setItems([])
    // Stale-while-revalidate (P4/P5/P9) — paint the last-known counts for
    // this workspace immediately instead of a blank/"—" sidebar; the
    // `loadStats` fired right after (in parallel with `loadGallery`)
    // revalidates it in the background.
    const cached = workspaceId ? getCachedVideoPickerStats(videoPickerCountCacheKey(workspaceId)) : null
    setStats(cached?.stats ?? null)
    setTotal(cached?.stats.total ?? null)
  }, [open, initialSelection, workspaceId])

  useEffect(() => {
    if (!open || tab !== 'library') return
    void loadGallery()
  }, [open, tab, loadGallery])

  useEffect(() => {
    if (!open) return
    void loadStats()
  }, [open, loadStats])

  const onSearchChange = (value: string) => {
    setSearchDraft(value)
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    searchTimer.current = window.setTimeout(() => {
      setSearch(value)
      setPage(1)
    }, 300)
  }

  const pickFile = useCallback(
    (file: File | null) => {
      if (!file) return
      if (!isAcceptedFile(file)) {
        setUploadError(t('video:assets.uploadModal.unsupportedFormat'))
        return
      }
      setUploadError(null)
      setUploadFile(file)
      if (!uploadTitle) setUploadTitle(file.name.replace(/\.[^/.]+$/, ''))
    },
    [t, uploadTitle],
  )

  const handleUploadSubmit = async () => {
    if (!uploadFile) return
    setUploading(true)
    setUploadError(null)
    setUploadProgress(0)
    try {
      const { data } = await uploadVideoAsset(workspaceId, uploadFile, uploadTitle.trim() || undefined, setUploadProgress)
      const uploadedId = `upload:${data.asset_id}`
      setTab('library')
      setPage(1)
      setSearch('')
      setSearchDraft('')
      setSelectedId(uploadedId)
      await loadGallery()
      await loadStats()
      setUploadFile(null)
      setUploadTitle('')
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: { message?: string } | string } } })?.response?.data?.detail
      const text = typeof msg === 'string' ? msg : msg?.message
      setUploadError(text || (e as Error).message || t('video:assets.uploadModal.uploadFailed'))
    } finally {
      setUploading(false)
    }
  }

  const mobileUploadReady = Boolean(workspaceId)

  const finalizeUploadedVideo = useCallback(
    async (uploadedId: string) => {
      setSort('recent')
      setSearch('')
      setSearchDraft('')
      setPage(1)
      setSelectedId(uploadedId)
      await loadGallery()
      await loadStats()
      message.success(t('canvas:mediaCard.picker.upload.success'))
    },
    [loadGallery, loadStats, t],
  )

  const openNativeFilePicker = () => {
    if (!allowUpload || tileUploading) return
    tileFileInputRef.current?.click()
  }

  const openMobileCapture = () => {
    if (!mobileUploadReady) {
      message.error(t('canvas:mediaCard.picker.upload.mobileUnavailable'))
      return
    }
    if (mobileCaptureOpen) return
    mobilePollGenerationRef.current += 1
    mobileCompletionSessionRef.current = null
    knownVideoIdsRef.current = new Set()
    setMobileSessionMeta(null)
    setMobileCaptureOpen(true)
  }

  const handleTileFileSelected = async (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0] ?? null
    ev.target.value = ''
    if (!file) return
    if (!isAcceptedFile(file)) {
      message.error(t('video:assets.uploadModal.unsupportedFormat'))
      return
    }
    setTileUploading(true)
    try {
      const { data } = await uploadVideoAsset(
        workspaceId,
        file,
        file.name.replace(/\.[^/.]+$/, '') || undefined,
      )
      await finalizeUploadedVideo(`upload:${data.asset_id}`)
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: { message?: string } | string } } })?.response?.data?.detail
      const text = typeof msg === 'string' ? msg : msg?.message
      message.error(text || (e as Error).message || t('canvas:mediaCard.picker.upload.failed'))
    } finally {
      setTileUploading(false)
    }
  }

  useEffect(() => {
    if (!open || !mobileCaptureOpen || tab !== 'library') return
    if (!isMobileCaptureSessionPaired(mobileSessionMeta)) return

    const generation = mobilePollGenerationRef.current
    const activeSessionId = mobileSessionMeta!.sessionId
    const sessionCreatedAt = mobileSessionMeta!.createdAt
    let baselineReady = false
    let cancelled = false

    const tick = async () => {
      if (cancelled || generation !== mobilePollGenerationRef.current) return
      const meta = mobileSessionMetaRef.current
      if (!meta || meta.sessionId !== activeSessionId) return
      if (mobileCompletionSessionRef.current === activeSessionId) return

      let pollItems: VideoAssetItem[] = []
      try {
        const { data } = await listVideoAssets(workspaceId, {
          sort: 'recent',
          page: 1,
          page_size: pageSize,
        })
        pollItems = data.items
      } catch {
        return
      }
      if (cancelled || generation !== mobilePollGenerationRef.current) return
      if (mobileSessionMetaRef.current?.sessionId !== activeSessionId) return

      if (!baselineReady) {
        knownVideoIdsRef.current = new Set(pollItems.map((item) => item.id))
        baselineReady = true
        return
      }

      const newIds = filterSessionScopedNewPickerIds(pollItems, knownVideoIdsRef.current, sessionCreatedAt)
      if (newIds.length === 0) return
      if (mobileCompletionSessionRef.current === activeSessionId) return

      newIds.forEach((id) => knownVideoIdsRef.current.add(id))
      mobileCompletionSessionRef.current = activeSessionId
      await finalizeUploadedVideo(newIds[newIds.length - 1])
      setMobileCaptureOpen(false)
    }

    const timer = window.setInterval(() => {
      void tick()
    }, MOBILE_CAPTURE_POLL_MS)
    void tick()
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [finalizeUploadedVideo, mobileCaptureOpen, mobileSessionMeta, open, pageSize, tab, workspaceId])

  // VistoeraTASK-00133 (P12/P13) — after every in-flight delete settles
  // (success or failure), re-fetch the *current* page + stats from the
  // canonical dataset. Because the list query is offset-based, re-slicing
  // the (now smaller) dataset at the same offset is what actually pulls
  // whatever used to sit on the next page forward into this page's last
  // slot — no "reload entire library" and no separate refill math. A
  // failed delete self-corrects here too (the asset was never actually
  // removed server-side, so this same re-fetch restores it).
  const reconcileAfterDelete = useCallback(async () => {
    const [listResult] = await Promise.all([
      loadGallery({ silent: true }),
      loadStats({ silent: true }),
    ])
    if (listResult && typeof listResult.total === 'number') {
      const clamped = pickerPageAfterDelete(page, listResult.total, pageSize, hasUploadTilePagination)
      if (clamped !== page) {
        // Current page no longer exists (e.g. deleted the last asset on
        // the last page) — clamp down (P15). The page-change effect
        // re-fetches it.
        skipListLoadingOnceRef.current = true
        setPage(clamped)
      }
    }
  }, [loadGallery, loadStats, page, pageSize, hasUploadTilePagination])

  const handleDeleteAsset = useCallback(
    (item: VideoAssetItem) => {
      if (pendingDeleteIdsRef.current.has(item.id)) return
      pendingDeleteIdsRef.current.add(item.id)
      inFlightDeleteCountRef.current += 1

      // Instant optimistic feedback — every update uses the functional
      // setState form so two rapid sequential deletes (P14) each apply
      // against the latest queued state rather than a stale snapshot
      // captured at click time (this is what actually prevents the race).
      setItems((prev) => prev.filter((i) => i.id !== item.id))
      setTotal((prev) => (prev == null ? prev : Math.max(0, prev - 1)))
      setStats((prev) => decrementVideoPickerStats(prev, item))
      setSelectedId((prev) => (prev === item.id ? null : prev))
      setPlayRequestedFor((prev) => (prev === item.id ? null : prev))

      void deleteVideoAsset(workspaceId, item.id)
        .catch((err) => {
          const detail = (err as { response?: { data?: { detail?: { message?: string } | string } } })?.response
            ?.data?.detail
          const text = typeof detail === 'string' ? detail : detail?.message
          message.error(text || t('canvas:mediaCard.toast.deleteFailedRestored'))
          // No manual rollback: reconcileAfterDelete (always runs once
          // every in-flight delete has settled) re-fetches the canonical
          // page/stats, which restores this asset since the DELETE never
          // actually took effect server-side.
        })
        .finally(() => {
          pendingDeleteIdsRef.current.delete(item.id)
          inFlightDeleteCountRef.current = Math.max(0, inFlightDeleteCountRef.current - 1)
          if (inFlightDeleteCountRef.current === 0) {
            void reconcileAfterDelete()
          }
        })
    },
    [workspaceId, reconcileAfterDelete, t],
  )

  const handleClose = useCallback(() => {
    setSelectedId(null)
    setPlayRequestedFor(null)
    onCancel()
  }, [onCancel])

  const handleConfirm = () => {
    if (!selected) return
    onConfirm(selected)
  }

  const typeCounts: Record<TypeFilter, number | undefined> = useMemo(
    () => ({
      all: stats?.total,
      ai_generated: stats?.ai_generated,
      continuous_generated: stats?.continuous_generated,
      upload: stats?.upload,
      published: stats?.published,
    }),
    [stats],
  )

  const originCounts: Record<OriginFilter, number | undefined> = useMemo(
    () => ({
      all: stats?.total,
      upload: stats?.upload,
      vistoera_generated: stats?.original,
    }),
    [stats],
  )

  // VistoeraTASK-00133 (P3/P22) — while stats haven't resolved yet (or
  // failed), the sidebar must render "—", never a fabricated `0` that
  // looks identical to a genuinely empty workspace.
  const formatFacetCount = (value: number | undefined): string => {
    if (statsLoading || stats == null) return '—'
    return typeof value === 'number' ? value.toLocaleString() : '—'
  }

  const resetPage = () => setPage(1)

  // VistoeraTASK-00133 (P22/P23) — LOADING / ERROR / EMPTY / READY are
  // rendered as visibly distinct states. The gallery head text in
  // particular must never read "共 0 个视频" while a request is still
  // in flight or has failed — that was this task's original screenshot
  // bug. `total == null` (never loaded / errored) shows a neutral
  // "视频库" label instead of a false zero.
  const galleryHeadLabel =
    loading && items.length === 0
      ? t('canvas:mediaCard.picker.loading')
      : total == null
        ? t('canvas:mediaCard.picker.totalUnknown')
        : t('canvas:mediaCard.picker.totalVideos', { count: total })

  const selectItem = (id: string) => {
    setSelectedId(id)
    setPlayRequestedFor(null)
  }

  const renderCard = (item: VideoAssetItem) => {
    const poster = videoAssetPosterUrl(workspaceId, item)
    const isSelected = selectedId === item.id
    return (
      <button
        key={item.id}
        type="button"
        className={`vap-card${isSelected ? ' selected' : ''}`}
        data-testid="vap-card"
        data-asset-id={item.id}
        onClick={() => selectItem(item.id)}
      >
        <div className="vap-card-thumb">
          {poster ? <img src={poster} alt="" loading="lazy" /> : <PlayCircleFilled />}
          {isSelected ? (
            <span className="vap-card-check">
              <CheckOutlined />
            </span>
          ) : null}
          <span className="vap-card-duration">{formatDuration(item.duration_seconds)}</span>
          <span className="vap-card-badge">{sourceLabel(item)}</span>
          {item.can_delete_hint ? (
            <span onClick={(e) => e.stopPropagation()}>
              <VistoeraActionConfirm
                title={t('canvas:mediaCard.deleteConfirm.videoSingleTitle')}
                description={
                  <>
                    {t('canvas:mediaCard.deleteConfirm.videoSingleDescription')}
                    <br />
                    {t('canvas:mediaCard.picker.delete.filenameLabel', {
                      filename: item.filename || item.title || item.id,
                    })}
                  </>
                }
                confirmLabel={t('actions:delete')}
                tooltip={t('canvas:mediaCard.picker.delete.tooltip')}
                onConfirm={() => handleDeleteAsset(item)}
              >
                <span
                  className="vap-card-delete"
                  role="button"
                  tabIndex={0}
                  aria-label={t('canvas:mediaCard.picker.delete.aria')}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') ev.stopPropagation()
                  }}
                >
                  <DeleteOutlined />
                </span>
              </VistoeraActionConfirm>
            </span>
          ) : null}
        </div>
        <div className="vap-card-meta">
          <span className="vap-card-name" title={item.title}>{item.title || t('canvas:mediaCard.untitledVideo')}</span>
          <span className="vap-card-sub">{formatResolution(item.width, item.height)} · {formatBytes(item.size_bytes)}</span>
        </div>
      </button>
    )
  }

  const renderListRow = (item: VideoAssetItem) => {
    const poster = videoAssetPosterUrl(workspaceId, item)
    const isSelected = selectedId === item.id
    return (
      <button
        key={item.id}
        type="button"
        className={`vap-list-row${isSelected ? ' selected' : ''}`}
        data-testid="vap-list-row"
        data-asset-id={item.id}
        onClick={() => selectItem(item.id)}
      >
        <span className="vap-list-check">{isSelected ? <CheckOutlined /> : null}</span>
        <span className="vap-list-thumb">
          {poster ? <img src={poster} alt="" loading="lazy" /> : <PlayCircleFilled />}
        </span>
        <span className="vap-list-name" title={item.title}>{item.title || t('canvas:mediaCard.untitledVideo')}</span>
        <span className="vap-list-type">{sourceLabel(item)}</span>
        <span className="vap-list-source">{originLabel(item)}</span>
        <span className="vap-list-duration">{formatDuration(item.duration_seconds)}</span>
        <span className="vap-list-resolution">{formatResolution(item.width, item.height)}</span>
        {item.can_delete_hint ? (
          <span onClick={(e) => e.stopPropagation()}>
            <VistoeraActionConfirm
              title={t('canvas:mediaCard.deleteConfirm.videoSingleTitle')}
              description={
                <>
                  {t('canvas:mediaCard.deleteConfirm.videoSingleDescription')}
                  <br />
                  {t('canvas:mediaCard.picker.delete.filenameLabel', {
                    filename: item.filename || item.title || item.id,
                  })}
                </>
              }
              confirmLabel={t('actions:delete')}
              tooltip={t('canvas:mediaCard.picker.delete.tooltip')}
              onConfirm={() => handleDeleteAsset(item)}
            >
              <span
                className="vap-list-delete"
                role="button"
                tabIndex={0}
                aria-label={t('canvas:mediaCard.picker.delete.aria')}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') ev.stopPropagation()
                }}
              >
                <DeleteOutlined />
              </span>
            </VistoeraActionConfirm>
          </span>
        ) : null}
      </button>
    )
  }

  const detailPlaybackUrl = selected ? videoAssetPlaybackUrl(workspaceId, selected) : null
  const detailPoster = selected ? videoAssetPosterUrl(workspaceId, selected) : undefined

  return (
    <Modal
      open={open}
      onCancel={uploading ? undefined : handleClose}
      footer={null}
      width="min(96vw, 1280px)"
      className="vap-modal"
      rootClassName="tc-video-asset-picker-modal vap-modal-root"
      destroyOnClose
      closable={false}
      maskClosable
      keyboard
      zIndex={2000}
    >
      <div className="vap-shell">
        <div className="vap-header">
          <h2 className="vap-title">{modalTitle}</h2>
          <button type="button" className="vap-close-btn" onClick={handleClose} aria-label={t('actions:cancel')}>
            <CloseOutlined />
          </button>
        </div>

        <div className="vap-tabs">
          <button type="button" className={`vap-tab${tab === 'library' ? ' active' : ''}`} onClick={() => setTab('library')}>
            {t('canvas:mediaCard.videoLibraryTab')}
          </button>
          <button type="button" className={`vap-tab${tab === 'upload' ? ' active' : ''}`} onClick={() => setTab('upload')}>
            {t('canvas:mediaCard.uploadVideoTab')}
          </button>
        </div>

        {tab === 'library' ? (
          <>
            <div className="vap-toolbar">
              <Input
                className="vap-search"
                allowClear
                placeholder={t('canvas:mediaCard.picker.searchPlaceholder')}
                value={searchDraft}
                onChange={(e) => onSearchChange(e.target.value)}
                data-testid="vap-search"
              />
              <Select
                value={sort}
                onChange={(v) => {
                  setSort(v)
                  resetPage()
                }}
                style={{ width: 150 }}
                options={[
                  { value: 'recent', label: t('video:assets.filters.sortRecent') },
                  { value: 'oldest', label: t('video:assets.filters.sortOldest') },
                ]}
              />
              <Button type={viewMode === 'grid' ? 'primary' : 'default'} icon={<AppstoreOutlined />} onClick={() => setViewMode('grid')} aria-label={t('video:assets.view.gridAria')} />
              <Button type={viewMode === 'list' ? 'primary' : 'default'} icon={<UnorderedListOutlined />} onClick={() => setViewMode('list')} aria-label={t('video:assets.view.listAria')} />
            </div>

            <div className="vap-body">
              <aside className="vap-sidebar">
                <div className="vap-filter-group">
                  <div className="vap-filter-title">{t('canvas:mediaCard.picker.resourceType')}</div>
                  {(
                    [
                      ['all', t('video:assets.filters.all')],
                      ['ai_generated', t('video:assets.sourceKinds.ai_generated')],
                      ['continuous_generated', t('video:assets.sourceKinds.continuous_generated')],
                      ['upload', t('video:assets.sourceKinds.upload')],
                      ['published', t('video:assets.filters.published')],
                    ] as Array<[TypeFilter, string]>
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`vap-filter-item${typeFilter === value ? ' active' : ''}`}
                      onClick={() => {
                        setTypeFilter(value)
                        resetPage()
                      }}
                    >
                      <span>{label}</span>
                      <span className="vap-filter-count">{formatFacetCount(typeCounts[value])}</span>
                    </button>
                  ))}
                </div>
                <div className="vap-filter-group">
                  <div className="vap-filter-title">{t('canvas:mediaCard.picker.source')}</div>
                  {(
                    [
                      ['all', t('canvas:mediaCard.picker.allSources')],
                      ['upload', t('video:assets.sources.upload')],
                      ['vistoera_generated', t('video:assets.sources.vistoera_generated')],
                    ] as Array<[OriginFilter, string]>
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`vap-filter-item${originFilter === value ? ' active' : ''}`}
                      onClick={() => {
                        setOriginFilter(value)
                        resetPage()
                      }}
                    >
                      <span>{label}</span>
                      <span className="vap-filter-count">{formatFacetCount(originCounts[value])}</span>
                    </button>
                  ))}
                </div>
                <div className="vap-filter-group">
                  <div className="vap-filter-title">{t('video:assets.aside.duration')}</div>
                  {(
                    [
                      ['all', t('video:assets.filters.allDuration')],
                      ['short', t('video:assets.filters.short')],
                      ['medium', t('video:assets.filters.medium')],
                      ['long', t('video:assets.filters.long')],
                    ] as Array<[DurationFilter, string]>
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`vap-filter-item${durationFilter === value ? ' active' : ''}`}
                      onClick={() => {
                        setDurationFilter(value)
                        resetPage()
                      }}
                    >
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                <div className="vap-filter-group">
                  <div className="vap-filter-title">{t('video:assets.aside.resolution')}</div>
                  {(
                    [
                      ['all', t('video:assets.filters.all')],
                      ['4k', '4K'],
                      ['1080p', '1080P'],
                      ['720p', '720P'],
                      ['other', t('video:assets.filters.resolutionOther')],
                    ] as Array<[ResolutionFilter, string]>
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`vap-filter-item${resolutionFilter === value ? ' active' : ''}`}
                      onClick={() => {
                        setResolutionFilter(value)
                        resetPage()
                      }}
                    >
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </aside>

              <section className="vap-gallery">
                <div className="vap-gallery-head">{galleryHeadLabel}</div>
                <div className="vap-gallery-scroll">
                  {error ? (
                    <Empty description={error}>
                      <Button icon={<ReloadOutlined />} onClick={() => void loadGallery()}>
                        {t('actions:retry')}
                      </Button>
                    </Empty>
                  ) : loading && items.length === 0 && !showUploadTile ? (
                    <div className="vap-skeleton-grid">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="vap-skeleton-card" />
                      ))}
                    </div>
                  ) : items.length === 0 && !showUploadTile ? (
                    <Empty description={t('canvas:mediaCard.noVideosFound')} />
                  ) : viewMode === 'grid' ? (
                    <div className="vap-grid" data-testid="vap-grid" data-vap-grid="true">
                      {showUploadTile ? (
                        <AssetUploadActionTile
                          layout="grid"
                          mobileLabel={t('canvas:mediaCard.picker.upload.mobile')}
                          localLabel={t('canvas:mediaCard.picker.upload.local')}
                          tileLabel={t('canvas:mediaCard.picker.upload.tileLabel')}
                          onMobileClick={openMobileCapture}
                          onLocalClick={openNativeFilePicker}
                          mobileDisabled={!mobileUploadReady || tileUploading}
                          localDisabled={tileUploading}
                          testIdPrefix="video-asset-picker"
                          markerPrefix="vap"
                          gridMinHeight={188}
                        />
                      ) : null}
                      {items.map(renderCard)}
                    </div>
                  ) : (
                    <div className="vap-list" data-testid="vap-list">
                      {showUploadTile ? (
                        <AssetUploadActionTile
                          layout="list"
                          mobileLabel={t('canvas:mediaCard.picker.upload.mobile')}
                          localLabel={t('canvas:mediaCard.picker.upload.local')}
                          tileLabel={t('canvas:mediaCard.picker.upload.tileLabel')}
                          onMobileClick={openMobileCapture}
                          onLocalClick={openNativeFilePicker}
                          mobileDisabled={!mobileUploadReady || tileUploading}
                          localDisabled={tileUploading}
                          testIdPrefix="video-asset-picker"
                          markerPrefix="vap"
                        />
                      ) : null}
                      {items.map(renderListRow)}
                    </div>
                  )}
                </div>
              </section>

              <aside className="vap-detail">
                {!selected ? (
                  <Empty description={t('canvas:mediaCard.picker.selectForDetails')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <>
                    <div className="vap-detail-preview">
                      {detailPlaybackUrl && playRequestedFor === selected.id ? (
                        // VistoeraTASK-00133 (P20/P25) — the actual video
                        // element (and therefore any video-binary/stream
                        // request) is only ever mounted after the user
                        // explicitly clicks "play" below; merely selecting
                        // a card to view its metadata issues zero such
                        // requests.
                        <video
                          key={selected.id}
                          src={detailPlaybackUrl}
                          poster={detailPoster}
                          controls
                          autoPlay
                          preload="none"
                          playsInline
                        />
                      ) : detailPoster ? (
                        <button
                          type="button"
                          className="vap-detail-play-btn"
                          data-testid="vap-detail-play"
                          aria-label={t('canvas:mediaCard.picker.playVideo')}
                          onClick={() => detailPlaybackUrl && setPlayRequestedFor(selected.id)}
                          disabled={!detailPlaybackUrl}
                        >
                          <img src={detailPoster} alt={selected.title} loading="lazy" />
                          <span className="vap-detail-play-overlay">
                            <PlayCircleFilled />
                          </span>
                        </button>
                      ) : (
                        <PlayCircleFilled style={{ fontSize: 32, opacity: 0.45 }} />
                      )}
                    </div>
                    <div className="vap-detail-name">{selected.title || t('canvas:mediaCard.untitledVideo')}</div>
                    {selected.filename ? <div className="vap-detail-filename">{selected.filename}</div> : null}
                    <span className="vap-detail-badge">{sourceLabel(selected)}</span>
                    <div className="vap-detail-section">
                      <h4>{t('canvas:mediaCard.picker.basicInfo')}</h4>
                      <div className="vap-kv">
                        <span>{t('video:assets.table.duration')}</span>
                        <span>{formatDuration(selected.duration_seconds)}</span>
                        <span>{t('video:assets.table.resolutionAndSize')}</span>
                        <span>{formatResolution(selected.width, selected.height)}</span>
                        <span>{t('canvas:mediaCard.picker.fileSize')}</span>
                        <span>{formatBytes(selected.size_bytes)}</span>
                        <span>{t('video:assets.table.source')}</span>
                        <span>{originLabel(selected)}</span>
                        <span>{t('video:assets.table.type')}</span>
                        <span>{sourceLabel(selected)}</span>
                        <span>{t('video:assets.table.createdAt')}</span>
                        <span>{formatCreatedAt(selected.created_at, i18n.language)}</span>
                      </div>
                    </div>
                  </>
                )}
              </aside>
            </div>

            <div className="vap-footer">
              <div className="vap-footer-left">
                {t('canvas:mediaCard.picker.selectedCount', { count: selected ? 1 : 0 })}
              </div>
              <div className="vap-footer-center">
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={total == null ? 0 : pickerAntdPaginationTotal(total, pageSize, hasUploadTilePagination)}
                  showSizeChanger
                  pageSizeOptions={PAGE_SIZE_OPTIONS.map(String)}
                  onChange={(p, ps) => {
                    setPage(p)
                    setPageSize(ps)
                  }}
                  showTotal={() =>
                    total == null ? '—' : `${page} / ${pickerTotalPages(total, pageSize, hasUploadTilePagination)}`
                  }
                  size="small"
                  data-testid="vap-pagination"
                />
              </div>
              <div className="vap-footer-right">
                <Button onClick={handleClose}>{t('actions:cancel')}</Button>
                <Button type="primary" disabled={!selected} onClick={handleConfirm} data-testid="vap-confirm">
                  {t('canvas:mediaCard.picker.confirmSelection')}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="vap-upload-pane">
              <div
                role="button"
                tabIndex={0}
                className="vap-upload-drop"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.currentTarget.classList.add('is-dragover')
                }}
                onDragLeave={(e) => e.currentTarget.classList.remove('is-dragover')}
                onDrop={(e) => {
                  e.preventDefault()
                  e.currentTarget.classList.remove('is-dragover')
                  pickFile(e.dataTransfer.files?.[0] ?? null)
                }}
              >
                <CloudUploadOutlined style={{ fontSize: 40 }} />
                <p>{uploading ? t('canvas:mediaCard.uploadingVideo') : t('video:assets.uploadModal.dropHint')}</p>
                <p className="vap-upload-hint">{t('video:assets.uploadModal.formatHint')}</p>
                {uploadFile ? <div className="vap-upload-filename">{uploadFile.name}</div> : null}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*,.mp4,.mov,.webm"
                style={{ display: 'none' }}
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              />
              {uploading ? <Progress percent={uploadProgress} style={{ marginTop: 12, maxWidth: 520 }} /> : null}
              {uploadError ? <div className="vap-upload-error">{uploadError}</div> : null}
            </div>
            <div className="vap-footer">
              <div />
              <div />
              <div className="vap-footer-right">
                <Button onClick={handleClose} disabled={uploading}>{t('actions:cancel')}</Button>
                <Button type="primary" disabled={!uploadFile || uploading} loading={uploading} onClick={handleUploadSubmit}>
                  {t('video:assets.uploadModal.upload')}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
      <input
        ref={tileFileInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
        style={{ display: 'none' }}
        data-vap-upload-input="true"
        onChange={(e) => void handleTileFileSelected(e)}
      />
      <MobileCaptureQrModal
        open={mobileCaptureOpen}
        workspaceId={workspaceId}
        targetFlow="video_asset_library"
        uploadHintKey="mobileCapture.uploadHintVideoAssetLibrary"
        onClose={() => setMobileCaptureOpen(false)}
        onSessionMetaChange={setMobileSessionMeta}
      />
    </Modal>
  )
}
